import {useCallback, useEffect, useState} from 'react';
import useTranslation from 'next-translate/useTranslation';

import HelpPanel, {
  HelpPanelSessionProvider,
  VisitHelpCenterCta,
} from '@/components/features/help/HelpPanel';
import {Tabs} from '@/components/ui/Tabs';
import useIdentity from '@/hooks/useIdentity';
import useMediaQuery from '@/hooks/useMediaQuery';
import useTracking from '@/hooks/useTracking';
import {media} from '@/styles/recipeMq';
import ContactChannels from '../ContactChannels';
import SupportMessageModal, {useSubmitSupportCase} from '../SupportMessage';

const HELP_TAB = 'help';
const CONTACT_TAB = 'contact';

type HelpPageContentProps = {
  experimentVariation: string;
};

const HelpPageContent: React.FC<HelpPageContentProps> = ({experimentVariation}) => {
  const {t} = useTranslation('common');
  const {track, trackClick} = useTracking();
  const {data} = useIdentity();
  const isWideLayout = useMediaQuery(media.large);
  const submitSupportCase = useSubmitSupportCase();

  const [activeTab, setActiveTab] = useState(HELP_TAB);
  const [isSupportMessageOpen, setIsSupportMessageOpen] = useState(false);

  useEffect(() => {
    track('shown-contact-us', {
      page: window.location.pathname,
      misc_json: JSON.stringify({variant: 'page', experiment_variation: experimentVariation}),
    });
  }, [track, experimentVariation]);

  const selectTab = useCallback(
    (tab: string) => {
      setActiveTab(tab);
      trackClick('help-page-tab', {misc_json: JSON.stringify({tab})});
    },
    [trackClick],
  );

  /*
   * Above `desktop` both panels show side by side and the tab list is hidden, so the tab roles would
   * describe a widget with no tabs. Radix spreads consumer props last, which lets us drop them.
   */
  const plainRegionProps = isWideLayout
    ? {role: undefined, tabIndex: undefined, 'aria-labelledby': undefined}
    : {};

  return (
    <>
      <div className="mx-auto max-w-7xl px-4 pb-8 pt-4 tablet:px-6 tablet:pb-12 tablet:pt-6 desktop:py-12">
        <HelpPanelSessionProvider>
          <Tabs
            className="flex flex-col gap-4"
            data-testid="help-page-content"
            onValueChange={selectTab}
            value={activeTab}
          >
            <h1 className="text-2xl font-bold text-peppercorn-800 desktop:text-3xl">
              {t(
                isWideLayout || activeTab === HELP_TAB
                  ? 'components.ContactUs.help.title'
                  : 'components.ContactUs.channels.liveSupport',
              )}
            </h1>

            <Tabs.List className="desktop:hidden">
              <Tabs.Trigger className="h-14 flex-1 justify-center" value={HELP_TAB}>
                {t('components.ContactUs.help.tabs.help')}
              </Tabs.Trigger>
              <Tabs.Trigger className="h-14 flex-1 justify-center" value={CONTACT_TAB}>
                {t('components.ContactUs.help.tabs.contact')}
              </Tabs.Trigger>
            </Tabs.List>

            <div className="desktop:flex desktop:items-start desktop:gap-8">
              <Tabs.Content
                className="flex flex-col gap-6 pt-2 data-[state=inactive]:hidden desktop:!flex desktop:min-w-0 desktop:flex-1 desktop:pt-0"
                forceMount
                value={HELP_TAB}
                {...plainRegionProps}
              >
                <HelpPanel
                  experimentVariation={experimentVariation}
                  layout="page"
                  renderCta={false}
                  showHeading={false}
                />
                <div className="sticky bottom-0 -mx-4 bg-white px-4 py-4 desktop:static desktop:mx-0 desktop:p-0">
                  <VisitHelpCenterCta />
                </div>
              </Tabs.Content>

              <Tabs.Content
                className="pt-2 data-[state=inactive]:hidden desktop:!block desktop:w-[378px] desktop:shrink-0 desktop:rounded-2xl desktop:border desktop:border-peppercorn-100 desktop:p-8 desktop:pt-8"
                forceMount
                value={CONTACT_TAB}
                {...plainRegionProps}
              >
                <h2 className="mb-8 hidden text-xl font-bold text-peppercorn-800 desktop:block">
                  {t('components.ContactUs.channels.liveSupport')}
                </h2>
                <ContactChannels
                  experimentVariation={experimentVariation}
                  onStartSupportMessage={() => setIsSupportMessageOpen(true)}
                  showHeading={false}
                  variant="page"
                />
              </Tabs.Content>
            </div>
          </Tabs>
        </HelpPanelSessionProvider>
      </div>

      <SupportMessageModal
        fieldContext={{
          fullName: data?.me?.consumerAccount?.fullName ?? '',
          email: data?.me?.consumerAccount?.email ?? '',
        }}
        onClose={() => setIsSupportMessageOpen(false)}
        onSubmit={submitSupportCase}
        open={isSupportMessageOpen}
        surface="contact_us_page"
      />
    </>
  );
};

export default HelpPageContent;
